import { area, centroid, dedupe, ensureCCW, validatePolygon, type Pt } from "../math/polygon";

export interface Section {
  polygon: Pt[]; area: number; centroid: Pt;
  baseY: number; toe: Pt; heel: Pt; B: number; yTop: number;
  /** Eğik taban: tanα (topuk alçak; 0 = yatay). B yatay izdüşümdür; taban boyu B/cosα. baseY = topuğun (en alt nokta) kotu. */
  baseSlope: number;
  alpha: number;                 // rad, düşeyden; + → dolgu düzlemin üstünde oturur
  /** Arka yüz kritik kama açısından yatık → düzlem α_cr'de dolgu içinden geçirildi (aradaki zemin duvarla birlikte) */
  alphaCapped: boolean;
  psi: number;                   // rad, TBDY ψ = π/2 − α
  planeXAt: (y: number) => number;
  planeTop: Pt;                  // dolgu yüzeyi ile düzlemin kesişimi (x_c, y_c)
  Hp: number;                    // itki yüksekliği = y_c − baseY
  topBack: Pt;                   // duvarın tepe arka köşesi
  wedge: Pt[]; wedgeArea: number;
  backChain: Pt[];               // arka sınır zinciri: topuk → … → tepe arka köşe (CCW)
  surfaceYAt: (x: number) => number;   // dolgu yüzeyi kotu (tepe arka köşeden β ile)
}

const TOL = 1e-6;
/** Eğik taban algılama üst sınırı (tanα); parametrik girdi 0,3 ile sınırlı, çokgende daha dik taban yatay taban sayılmaz */
const MAX_BASE_SLOPE = 0.5;

/**
 * İkinci kayma yüzeyi açısı (düşeyden): α_cr = 45° − φ/2 + (β − asin(sinβ / sinφ))/2. Arka yüzü bundan yatık duvarda
 * dolgu kaması yüze değmez; kama topuktan α_cr düzlemiyle kayar, yüz ile düzlem arasındaki zemin duvarla birlikte hareket eder.
 * Sınırsız α'da ψ − θ − δ → 0 olur, Coulomb/M-O tekilleşir (Ka ≫ 1, düşey bileşen haksız yere direnç sağlar).
 */
export function criticalPlaneAngle(phiRad: number, betaRad: number): number {
  if (phiRad <= 0) return Math.PI / 4;
  const s = Math.min(1, Math.sin(betaRad) / Math.sin(phiRad));
  return Math.PI / 4 - phiRad / 2 + (betaRad - Math.asin(s)) / 2;
}

/** Çokgen (herhangi bir taban kotunda) + dolgu eğimi β (rad) → kesit bilgileri. `phiRad` (dolgu φ) verilirse sanal düzlem α_cr ile sınırlanır. */
export function buildSection(polygonIn: Pt[], betaRad: number, phiRad?: number): Section {
  const errors = validatePolygon(polygonIn);
  if (errors.length) throw new Error("Geçersiz geometri: " + errors.join("; "));
  const polygon = ensureCCW(dedupe(polygonIn));
  const baseY = Math.min(...polygon.map((p) => p.y));
  const yTop = Math.max(...polygon.map((p) => p.y));
  const baseIdx = polygon.map((p, i) => ({ p, i })).filter(({ p }) => Math.abs(p.y - baseY) < TOL);
  let toe = baseIdx.reduce((a, b) => (b.p.x < a.p.x ? b : a)).p;
  const heelEntry = baseIdx.reduce((a, b) => (b.p.x > a.p.x ? b : a));
  const heel = heelEntry.p;
  // Eğik taban (topuk alçak): en alt nokta tek ve CCW'de önceki köşe (burun) sağa doğru hafifçe inen kenarla bağlıysa taban o kenardır
  let baseSlope = 0;
  if (baseIdx.length === 1) {
    const prev = polygon[(heelEntry.i - 1 + polygon.length) % polygon.length]!;
    const dx = heel.x - prev.x, dy = prev.y - heel.y;
    if (dx > TOL && dy > TOL && dy / dx <= MAX_BASE_SLOPE + 1e-9) { toe = prev; baseSlope = dy / dx; }
  }
  const B = heel.x - toe.x;

  // Arka sınır zinciri: topuktan CCW yönde tepe arka köşesine kadar
  const n = polygon.length;
  const chain: Pt[] = [];
  let topBack: Pt = heel;
  for (let k = 1; k <= n; k++) {
    const v = polygon[(heelEntry.i + k) % n]!;
    chain.push(v);
    if (v.y >= yTop - TOL) { topBack = v; break; }
  }

  let alpha = Number.POSITIVE_INFINITY;
  for (const v of chain) {
    const dy = v.y - baseY;
    if (dy > TOL) alpha = Math.min(alpha, Math.atan((heel.x - v.x) / dy));
  }
  if (!Number.isFinite(alpha)) alpha = 0;
  let alphaCapped = false;
  if (phiRad !== undefined) { const cr = criticalPlaneAngle(phiRad, betaRad); if (alpha > cr + 1e-9) { alpha = cr; alphaCapped = true; } }
  const tanA = Math.tan(alpha);
  const planeXAt = (y: number): number => heel.x - (y - baseY) * tanA;

  const tanB = Math.tan(betaRad);
  const denom = 1 + tanA * tanB;
  if (denom < 0.05) throw new Error("Dolgu şevi ile sanal arka düzlem kesişmiyor (β çok büyük veya arka yüz çok sarkık)");
  const yC = baseY + ((yTop - baseY) + (heel.x - topBack.x) * tanB) / denom;
  const planeTop: Pt = { x: planeXAt(yC), y: yC };
  const Hp = yC - baseY;

  const wedgeRaw: Pt[] = [heel, ...chain];
  if (Math.hypot(planeTop.x - topBack.x, planeTop.y - topBack.y) > TOL) wedgeRaw.push(planeTop);
  const wedge = ensureCCW(dedupe(wedgeRaw));
  const wedgeArea = wedge.length >= 3 ? area(wedge) : 0;

  const tb = topBack;
  const surfaceYAt = (x: number): number => yTop + (x - tb.x) * tanB;

  return {
    polygon, area: area(polygon), centroid: centroid(polygon),
    baseY, toe, heel, B, yTop, baseSlope,
    alpha, alphaCapped, psi: Math.PI / 2 - alpha, planeXAt, planeTop, Hp, topBack,
    wedge, wedgeArea,
    backChain: [heel, ...chain], surfaceYAt,
  };
}
